import {
  identify,
  peopleSet,
  peopleSetOnce,
  setGroup,
  syncSuperProps,
} from "@/analytics/core";
import { useUserInfo } from "@/api/user";
import { config } from "@/config";
import { PERMISSIONS } from "@/constants/permissions";
import { desktopShellVersion, isDesktopShell } from "@/native/desktopShell";
import { getPlatform, isRealNativePlatform } from "@/native/platform";
import { useEffect, useRef } from "react";

export function currentPlatform(): string {
  if (isDesktopShell()) return "desktop";
  if (isRealNativePlatform()) return getPlatform();
  return "web";
}

/**
 * Identity, profile, group and persistent super properties, synced from
 * userInfo. Mounted inside TrackUserProvider. A last-payload hash guards
 * against StrictMode double effects and the ["userInfo"] refetch-on-focus
 * re-sending identical engage calls.
 */
export function AnalyticsIdentitySync() {
  const { data: userInfo } = useUserInfo();
  const lastPayload = useRef<string>("");

  useEffect(() => {
    if (!userInfo) return;

    const platform = currentPlatform();
    const org = userInfo.organization;
    const sub = userInfo.subscription;
    const orgUser = userInfo.organization_user;

    const superProps = {
      platform,
      desktop_version: desktopShellVersion() ?? undefined,
      app_env: config.appEnv,
      app_release: config.github.sha.slice(0, 7),
      organization_id: org?.id,
      org_role: userInfo.permissions.includes(PERMISSIONS.ORG_ADMIN.ALL)
        ? "admin"
        : "member",
      plan_name: sub?.plan_name,
      subscription_status: sub?.status,
      is_trialing: sub?.status === "trialing",
      seat_count: sub?.seat_count,
      has_payment_method: Boolean(sub?.default_payment_method_id),
    };

    const profile = {
      $email: userInfo.user.email,
      $name: userInfo.user.name,
      organization_id: org?.id,
      organization: org?.name,
      job_title: orgUser?.metadata?.job_title,
      department: orgUser?.metadata?.department,
      experience_level: orgUser?.onboarding_status?.experience_level,
      onboarding_status: orgUser?.onboarding_status?.global,
      signup_provider: userInfo.user.provider,
      last_platform: platform,
    };

    const group = org
      ? {
          $name: org.name,
          plan_name: sub?.plan_name,
          subscription_status: sub?.status,
          seat_count: sub?.seat_count,
          created_at: org.created_at,
        }
      : null;

    const payload = JSON.stringify([
      userInfo.user.id,
      superProps,
      profile,
      group,
    ]);
    if (payload === lastPayload.current) return;
    lastPayload.current = payload;

    identify(userInfo.user.id);
    // Persistent props survive in localStorage, so keys whose value went
    // away (org switch, plan loss) must be unregistered, not just skipped.
    syncSuperProps(superProps);
    peopleSet(profile);
    peopleSetOnce({
      $created: userInfo.user.created_at,
      first_platform: platform,
    });
    if (org) {
      setGroup("organization_id", org.id, group!);
    }
  }, [userInfo]);

  return null;
}
